import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabase';
import { SESSION_STATUSES } from '../utils/constants';
import './PaymentSuccessPage.css';

function PaymentSuccessPage() {
  const { loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('verifying'); // 'verifying' | 'confirmed' | 'not_found' | 'error'
  const [sessionRequest, setSessionRequest] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (authLoading) return;
    if (!sessionId) {
      setStatus('not_found');
      return;
    }

    const verify = async () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('PaymentSuccessPage: verifying stripe session_id:', sessionId);
      }
      try {
        const { data, error } = await supabase
          .from('session_requests')
          .select(`
            id, status, sport, proposed_date, proposed_time, proposed_location,
            mentor:profiles!session_requests_mentor_id_fkey(first_name, last_name)
          `)
          .eq('stripe_session_id', sessionId)
          .single();

        if (error || !data) {
          pollCount.current += 1;
          if (pollCount.current < 5) {
            setTimeout(verify, 2000);
          } else {
            setStatus('not_found');
          }
          return;
        }

        if (data.status === SESSION_STATUSES.PAID || data.status === SESSION_STATUSES.COMPLETED) {
          setSessionRequest(data);
          setStatus('confirmed');
          // Redirect after 4 seconds
          setTimeout(() => { window.location.href = '/dashboard'; }, 4000);
        } else {
          // Not yet paid — poll
          pollCount.current += 1;
          if (pollCount.current < 5) {
            setTimeout(verify, 2000);
          } else {
            setStatus('not_found');
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('PaymentSuccessPage verify error:', err.message);
        }
        pollCount.current += 1;
        if (pollCount.current < 5) {
          setTimeout(verify, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    verify();
  }, [sessionId, navigate, authLoading]);

  if (status === 'verifying') {
    return (
      <div className="payment-success-page">
        <div className="payment-success-card">
          <div className="app-loading-spinner payment-verify-spinner" />
          <h2 className="payment-success-title">Confirming your payment…</h2>
          <p className="payment-success-sub">Please wait while we verify your booking fee.</p>
        </div>
      </div>
    );
  }

  if (status === 'not_found' || status === 'error') {
    return (
      <div className="payment-success-page">
        <div className="payment-success-card">
          <div className="payment-success-icon payment-success-icon-warn">⚠️</div>
          <h2 className="payment-success-title">Payment received</h2>
          <p className="payment-success-sub">
            Your payment went through, but we're still processing your booking.
            Check your dashboard in a moment — your session should appear there.
          </p>
          <Link to="/dashboard" className="btn btn-primary" style={{ marginTop: '1rem' }}>
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-page">
      <div className="payment-success-card">
        <div className="payment-success-icon">✓</div>
        <h1 className="payment-success-title">Session Confirmed!</h1>
        <p className="payment-success-sub">
          Your $4.00 booking fee was processed successfully.
        </p>

        {sessionRequest && (
          <div className="payment-success-details">
            <p className="payment-success-detail-row">
              <span>With</span>
              <strong>{sessionRequest.mentor ? `${sessionRequest.mentor.first_name} ${sessionRequest.mentor.last_name}` : ''}</strong>
            </p>
            <p className="payment-success-detail-row">
              <span>Sport</span>
              <strong>{sessionRequest.sport}</strong>
            </p>
            <p className="payment-success-detail-row">
              <span>Location</span>
              <strong>{sessionRequest.proposed_location}</strong>
            </p>
          </div>
        )}

        <div className="alert alert-success payment-success-reminder">
          Remember to bring cash or payment to your trainer for their session rate.
        </div>

        <p className="payment-success-redirect">
          Redirecting to your dashboard in 4 seconds…
        </p>

        <a href="/dashboard" className="btn btn-primary btn-full" style={{ marginTop: '0.75rem' }}>
          Go to Dashboard Now
        </a>
      </div>
    </div>
  );
}

export default PaymentSuccessPage;
